import { GoalWithHoldingSummary } from "creso-core/types";
import { Route } from "@/routes/(auth)/_headed/clients/$id/planning";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Button,
  Dialog,
  DialogTrigger,
  DialogHeader,
  DialogContent,
  DialogTitle,
  DialogDescription,
  VisuallyHidden,
  DialogFooter,
  cn,
} from "web-design-system";
import { CheckIcon, EllipsisVertical, InfoIcon, PlusIcon } from "lucide-react";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import Amount from "@funds/components/atoms/amount";
import { Link } from "@tanstack/react-router";
import { formatDistanceInDays } from "creso-core/utils";
import { useMemo, useState } from "react";
import { useDeleteGoal } from "creso-core/hooks";
import { handleError } from "@/utils/error";
import { addMonths } from "date-fns";
import { useTheme } from "@/theme";
import { useQueryClient } from "@tanstack/react-query";
import { GoalIllustration } from "../../config";
import { GoalProposalDialog } from "./goal-proposal-dialog";

export function GoalCard(props: { goal: GoalWithHoldingSummary }) {
  const navigate = Route.useNavigate();
  const futureValue = Number(props.goal.future_value ?? 0);

  const endDate = useMemo(() => {
    const createdAt = new Date(props.goal.created_at);
    return addMonths(createdAt, props.goal.duration_months || 0);
  }, [props.goal.created_at, props.goal.duration_months]);

  const progress = Math.round(props.goal.progress_percentage ?? 0);

  const { currentTheme } = useTheme();

  const illustration =
    GoalIllustration[currentTheme][
      props.goal?.category as keyof typeof GoalIllustration
    ];

  const gradient = useMemo(() => {
    if (currentTheme === "light") {
      return "linear-gradient(0deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.05) 100%), linear-gradient(161deg, rgba(255, 255, 255, 0.66) 12.55%, rgba(255, 255, 255, 0.00) 35.54%)";
    }
    return "linear-gradient(0deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.03) 100%), linear-gradient(161deg, rgba(0, 0, 0, 0.36) 7.24%, rgba(0, 0, 0, 0.00) 35.54%)";
  }, [currentTheme]);

  return (
    <div className="flex rounded-lg border border-border flex-col transition-all">
      <div
        style={{
          backgroundImage: `url(${illustration})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        className="h-40 w-full rounded-t-lg relative overflow-hidden"
      >
        <div
          className="absolute top-0 size-full"
          style={{ background: gradient }}
        />
        <div className="flex px-4 py-3 flex-row top-0 left-0 right-0 items-center absolute justify-between">
          <span className="font-semibold text-sm">{props.goal.name}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="-mr-2 -mt-1">
                <EllipsisVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() =>
                  navigate({
                    to: Route.fullPath,
                    search: {
                      client_id: props.goal.client,
                      goal_id: props.goal.id,
                      state: "create",
                      modal: "goals",
                    },
                  })
                }
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="text-destructive">
                <DeleteGoal
                  id={props.goal.id}
                  proposalsCount={props.goal.proposals_count}
                />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="p-4 bg-background rounded-b-lg flex flex-1">
        <ConditionalRenderer condition={props.goal.linked_holdings_count === 0}>
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex flex-row items-center justify-between">
              <span className="text-sm font-semibold">
                <Amount precision={0}>{futureValue}</Amount>
              </span>
              <span className="text-sm text-muted-foreground">
                {formatDistanceInDays(endDate)} left
              </span>
            </div>
            <Button
              variant="outline"
              onClick={() =>
                navigate({
                  to: Route.fullPath,
                  search: {
                    goal_id: props.goal.id,
                    client_id: props.goal.client,
                    state: "link",
                    modal: "goals",
                  },
                })
              }
            >
              Link holdings
            </Button>
          </div>
        </ConditionalRenderer>
        <ConditionalRenderer condition={props.goal.linked_holdings_count > 0}>
          <div className="flex flex-col flex-1 justify-between gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">
                <Amount precision={0}>{props.goal.total_current_value}</Amount>
                &nbsp;{"/"}&nbsp;
                <Amount precision={0}>{futureValue}</Amount>
              </span>
              <span className="text-sm text-muted-foreground">
                {formatDistanceInDays(endDate)}
              </span>
            </div>
            <div className="h-1.5 w-full bg-muted rounded-lg">
              <div
                style={{
                  width: `${Math.min(progress, 100)}%`,
                }}
                className={cn(
                  "h-full rounded-lg transition-all",
                  progress < 100 ? "bg-primary" : "bg-constructive"
                )}
              />
            </div>
            <div className="flex items-center justify-between">
              {progress >= 100 ? (
                <div className="text-sm flex items-center gap-0.75 text-constructive">
                  <CheckIcon className="size-4 mt-0.5" /> Completed
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {progress}%
                </span>
              )}
              <Link
                to={Route.fullPath}
                search={{
                  client_id: props.goal.client,
                  goal_id: props.goal.id,
                  state: "link",
                  modal: "goals",
                }}
                params={{ id: props.goal.client.toString() }}
                className="text-sm"
              >
                {props.goal.linked_holdings_count} holding(s) linked
              </Link>
            </div>
          </div>
        </ConditionalRenderer>
      </div>
      <div className="flex flex-row py-2 mx-2 items-center justify-end border-t border-border">
        <ConditionalRenderer condition={props.goal.proposals_count > 0}>
          <GoalProposalDialog goal={props.goal} />
        </ConditionalRenderer>
        <ConditionalRenderer condition={props.goal.proposals_count === 0}>
          <Button
            variant="ghost"
            className="px-2 h-auto"
            onClick={() =>
              navigate({
                to: Route.fullPath,
                search: {
                  client_id: props.goal.client,
                  goal_id: props.goal.id,
                  modal: "proposal",
                  state: "init",
                },
              })
            }
          >
            <PlusIcon />
            Create Proposal
          </Button>
        </ConditionalRenderer>
      </div>
    </div>
  );
}

function DeleteGoal(props: { id: number; proposalsCount?: number }) {
  const { mutateAsync: deleteGoal } = useDeleteGoal();
  const queryClient = useQueryClient();

  const handleDeleteGoal = async () => {
    try {
      await deleteGoal(props.id);
      if (props.proposalsCount) {
        queryClient.invalidateQueries({
          queryKey: ["proposals"],
        });
      }
    } catch (e) {
      handleError(e, "Failed to delete goal");
    }
  };

  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        className="hover:bg-muted w-full text-destructive flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0"
        onClick={() => setOpen(true)}
      >
        Delete
      </DialogTrigger>
      <DialogContent>
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>Delete Goal</DialogTitle>
          </DialogHeader>
          <DialogDescription>Are you sure</DialogDescription>
        </VisuallyHidden>
        <span className="text-lg font-semibold">
          Are you sure you want to delete this goal?
        </span>
        <span className="text-sm text-muted-foreground">
          The goal will be deleted. This action cannot be undone.
        </span>
        {props.proposalsCount && props.proposalsCount > 0 ? (
          <div className="text-sm flex items-center bg-card p-4 rounded-lg gap-2">
            <InfoIcon className="size-4" />
            <span>
              This goal has {props.proposalsCount} proposal(s) that will be
              deleted with it.
            </span>
          </div>
        ) : null}
        <DialogFooter>
          <Button onClick={handleDeleteGoal} variant="outline">
            Yes, Delete
          </Button>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
